import React from 'react';
import {View} from 'react-native';
import {isNull} from 'lodash';

import {Banner} from '/component/base/Banner/Banner';
import Button from '/component/base/Button';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {acceptDeclineReasonTestIDs, Shared} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import testProperties from '/util/testing/testProperties';

import {useAcceptDeclineBanner} from './AcceptDeclineBanner.hook';
import {styles} from './AcceptDeclineBanner.style';
import {
  generateOpenDeclinePopover,
  getAcceptByLocalTime,
} from './AcceptDeclineBanner.util';

interface AcceptDeclineBannerProps {
  popoverShow: (popoverComponent: any, popoverProps: any) => void;
  acceptBefore: string;
  updateCandidateStatus: () => void;
  getCandidateDeclineReasons: () => void;
  hasDeclineReasons: boolean;
  reportName: string;
}

export const AcceptDeclineBanner = (props: AcceptDeclineBannerProps) => {
  const {
    popoverShow,
    acceptBefore,
    updateCandidateStatus,
    getCandidateDeclineReasons,
    hasDeclineReasons,
    reportName,
  } = props;

  const {ts, tt} = useTranslation('AcceptDeclineBanner');
  const formattedAcceptByTime = getAcceptByLocalTime(acceptBefore);
  const showDay = getDateUtilDefaultInstance().checkDate(
    acceptBefore,
    reportName,
  );
  const {
    toggleExpand,
    expanded,
    onAcceptPress,
    isAcceptPressed,
    isDeclinePressed,
    handleDeclineCandidate,
  } = useAcceptDeclineBanner({
    updateCandidateStatus,
    getCandidateDeclineReasons,
    hasDeclineReasons,
  });

  return (
    <>
      {!isNull(acceptBefore) && showDay && (
        <View
          style={styles.container}
          {...testProperties(acceptDeclineReasonTestIDs.acceptDeclineBanner)}>
          <Banner style={{base: styles.base}} verticalBanner alertLevel="info">
            <TouchableOpacity
              style={styles.accordionHeader}
              onPress={toggleExpand}
              {...testProperties(null, true)}>
              <Icon name="alert" style={styles.alertButtonIcon} />
              <Text
                style={styles.accordionTitle}
                {...testProperties(
                  acceptDeclineReasonTestIDs.acceptDeclineBannerTitle,
                )}>
                {' '}
                {ts('Accordion Title', 'New Leads Pool Candidate')}
              </Text>
              <Icon
                name={expanded ? 'chevron-up' : 'chevron-down'}
                style={styles.accordionIcon}
              />
            </TouchableOpacity>
            {expanded && (
              <View style={styles.accordionBody}>
                <Text
                  style={styles.accordionBody}
                  {...testProperties(
                    acceptDeclineReasonTestIDs.acceptDeclineBannerDescription,
                  )}>
                  {tt(
                    'candidateAcceptDescription',
                    generateComponentCallback({
                      component: (
                        <>
                          Please accept by {formattedAcceptByTime} CT {showDay}{' '}
                          if you would like to accept them as a candidate, or
                          they will be reassigned. Contact info available after
                          acceptance.
                        </>
                      ),
                    }),
                  )}
                </Text>
                <Button
                  onPress={onAcceptPress}
                  disabled={isAcceptPressed}
                  style={{
                    base: styles.acceptButton,
                  }}
                  testID={
                    acceptDeclineReasonTestIDs.acceptDeclineBannerAcceptBtn
                  }>
                  {ts('accept button', 'Accept')}
                </Button>
                <Button
                  type="textLink"
                  disabled={isDeclinePressed}
                  onPress={generateOpenDeclinePopover(
                    {popoverShow},
                    handleDeclineCandidate,
                  )}>
                  <View
                    style={styles.declineButton}
                    {...testProperties(Shared.view)}>
                    <Icon name={'trash'} style={styles.trashIcon} />
                    <Text
                      style={styles.declineLabel}
                      testID={
                        acceptDeclineReasonTestIDs.acceptDeclineBannerDeclineBtn
                      }>
                      {ts('decline button', 'Decline candidate')}
                    </Text>
                  </View>
                </Button>
              </View>
            )}
          </Banner>
        </View>
      )}
    </>
  );
};
